// Client-side high-end certificate PDF via `window.print()`. Champagne-gold
// treatment on obsidian black: meant to be framed and hung in a kitchen.

export function printCertificate({ name, email, certificateNumber, certifiedAt }) {
  const win = window.open("", "_blank", "width=1000,height=780");
  if (!win) return;

  const displayName = name || (email ? email.split("@")[0] : "Chef");
  const dateStr = certifiedAt
    ? new Date(certifiedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString();

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Certified Culinary Specialist: ${displayName}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Inter+Tight:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background: #0b0b0d; color: #f5efe0; font-family: 'Inter Tight', -apple-system, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet {
    box-sizing: border-box;
    width: 297mm; height: 210mm;
    padding: 22mm 24mm;
    background:
      radial-gradient(ellipse at top left, rgba(232,207,154,0.14), transparent 55%),
      radial-gradient(ellipse at bottom right, rgba(200,169,106,0.10), transparent 55%),
      linear-gradient(180deg, #0d0c0a 0%, #08070a 100%);
    position: relative;
    overflow: hidden;
  }
  .frame {
    position: absolute; inset: 12mm;
    border: 1.5px solid rgba(232,207,154,0.35);
    border-radius: 4mm;
    pointer-events: none;
  }
  .frame::before, .frame::after {
    content: ''; position: absolute; inset: 3mm;
    border: 0.5px solid rgba(232,207,154,0.15);
    border-radius: 2.5mm;
  }
  .corners { position: absolute; inset: 12mm; pointer-events: none; }
  .corner {
    position: absolute; width: 22mm; height: 22mm;
    border: 1.5px solid rgba(232,207,154,0.85);
  }
  .corner.tl { top: -1px; left: -1px; border-right: none; border-bottom: none; }
  .corner.tr { top: -1px; right: -1px; border-left: none; border-bottom: none; }
  .corner.bl { bottom: -1px; left: -1px; border-right: none; border-top: none; }
  .corner.br { bottom: -1px; right: -1px; border-left: none; border-top: none; }
  .content { position: relative; text-align: center; padding: 0 12mm; }
  .crest {
    display: inline-flex; align-items: center; justify-content: center;
    width: 24mm; height: 24mm; border-radius: 50%;
    background: linear-gradient(160deg, rgba(232,207,154,0.28), rgba(200,169,106,0.05));
    border: 1.2px solid rgba(232,207,154,0.7);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 2px 12px rgba(200,169,106,0.28);
    margin: 6mm auto 8mm;
  }
  .crest svg { width: 12mm; height: 12mm; color: #f2dfb5; }
  .eyebrow {
    font-size: 10px; letter-spacing: 0.42em; text-transform: uppercase;
    color: #c8a96a;
  }
  h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-weight: 400;
    font-size: 56px;
    letter-spacing: -0.01em;
    line-height: 1.05;
    margin: 6mm 0 4mm;
    color: #f5efe0;
  }
  .subtitle {
    font-family: 'Fraunces', serif;
    font-style: italic;
    font-weight: 400;
    font-size: 22px;
    color: #c8a96a;
    margin: 0 0 10mm;
  }
  .recipient-eyebrow {
    font-size: 10px; letter-spacing: 0.32em; text-transform: uppercase;
    color: #8a8578; margin-bottom: 4mm;
  }
  .recipient {
    font-family: 'Fraunces', serif;
    font-weight: 400;
    font-size: 48px;
    letter-spacing: -0.01em;
    color: #f5efe0;
    margin: 0 0 6mm;
    padding: 0 6mm;
  }
  .rule {
    width: 40mm; height: 1px; background: #c8a96a; margin: 0 auto 8mm; opacity: 0.6;
  }
  .body {
    font-size: 12px; line-height: 1.7; color: #b8b1a3;
    max-width: 150mm; margin: 0 auto;
  }
  .footer {
    position: absolute; left: 24mm; right: 24mm; bottom: 22mm;
    display: flex; justify-content: space-between; align-items: end;
    font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
    color: #8a8578;
  }
  .footer .label { font-size: 9px; letter-spacing: 0.28em; color: #6f6a5e; margin-bottom: 2mm; }
  .footer .value { color: #f2dfb5; font-family: 'Fraunces', serif; font-size: 16px; text-transform: none; letter-spacing: 0; }
  .cert-no { font-family: 'Courier New', monospace; letter-spacing: 0.18em; color: #f2dfb5; }
  .print-btn {
    position: fixed; top: 12px; right: 12px; z-index: 100;
    background: #c8a96a; color: #0b0b0d; border: none; padding: 10px 20px;
    border-radius: 999px; font-weight: 600; font-size: 13px; cursor: pointer;
    font-family: 'Inter Tight', sans-serif;
  }
  @media print { .print-btn { display: none; } }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
  <div class="sheet">
    <div class="frame"></div>
    <div class="corners">
      <div class="corner tl"></div><div class="corner tr"></div>
      <div class="corner bl"></div><div class="corner br"></div>
    </div>
    <div class="content" style="padding-top: 18mm;">
      <div class="crest">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
          <line x1="6" y1="17" x2="18" y2="17"/>
        </svg>
      </div>
      <div class="eyebrow">CulinaireSpecialist • Est. 2026</div>
      <h1>Certificate of Precision</h1>
      <div class="subtitle">Certified Culinary Specialist</div>
      <div class="recipient-eyebrow">Awarded to</div>
      <div class="recipient">${displayName}</div>
      <div class="rule"></div>
      <div class="body">
        For demonstrated mastery of the CulinaireSpecialist precision cost engine: reconciling weight, volume, yield and labour to a trustworthy plate cost, and putting real menu data behind every number. This chef has passed the three-question Precision Quiz and completed a live Recipe-to-Inventory link, proving their kitchen data is honest and actionable.
      </div>
    </div>
    <div class="footer">
      <div>
        <div class="label">Awarded</div>
        <div class="value">${dateStr}</div>
      </div>
      <div style="text-align:center;">
        <div class="label">Signed</div>
        <div class="value" style="font-family:'Fraunces',serif; font-style:italic;">The Kitchen Concierge</div>
      </div>
      <div style="text-align:right;">
        <div class="label">Certificate №</div>
        <div class="value cert-no">${certificateNumber || "CS-0000"}</div>
      </div>
    </div>
  </div>
  <script>
    // Auto-open the print dialog once fonts are ready (small delay for reliability).
    window.addEventListener('load', () => setTimeout(() => { try { window.print(); } catch(_){} }, 600));
  </script>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
